import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, TrendingUp, Clock, Target, CheckCircle, Star, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SalesKPIs {
  totalProviders: number;
  activeProviders: number;
  newRegistrations7d: number;
  avgAcceptanceRate: number;
  totalOpps30d: number;
  deliveredOpps30d: number;
  monthRevenue: number;
  avgMatchScore: number;
}

function useSalesContent(t: (key: string) => string) {
  const ELEVATOR_PITCH = t("op.salesElevatorPitchText");
  const OBJECTION_RESPONSES = [
    { objection: t("op.salesObj1Q"), response: t("op.salesObj1A") },
    { objection: t("op.salesObj2Q"), response: t("op.salesObj2A") },
    { objection: t("op.salesObj3Q"), response: t("op.salesObj3A") },
    { objection: t("op.salesObj4Q"), response: t("op.salesObj4A") },
    { objection: t("op.salesObj5Q"), response: t("op.salesObj5A") },
  ];
  return { ELEVATOR_PITCH, OBJECTION_RESPONSES };
}

export default function OperatorSalesDashboard() {
  const { t } = useTranslation();
  const { ELEVATOR_PITCH, OBJECTION_RESPONSES } = useSalesContent(t);
  const [kpis, setKpis] = useState<SalesKPIs | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  useEffect(() => { loadKPIs(); }, []);

  async function loadKPIs() {
    try {
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 86400000).toISOString();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 86400000).toISOString();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

      const [providersRes, activeRes, regsRes, oppsRes, billingRes] = await Promise.all([
        supabase.from("provider_profiles").select("id, acceptance_rate"),
        supabase.from("provider_profiles").select("id").eq("is_approved", true).eq("is_suspended", false),
        supabase.from("provider_registrations").select("id").gte("created_at", sevenDaysAgo),
        supabase.from("opportunities").select("id, status, delivery_state, match_score").gte("created_at", thirtyDaysAgo),
        supabase.from("opportunity_billing_lines").select("final_cents").eq("month_key", monthKey),
      ]);

      const providers = providersRes.data || [];
      const opps = oppsRes.data || [];
      const avgAcceptance = providers.length > 0 ? providers.reduce((s, p: any) => s + (p.acceptance_rate || 0), 0) / providers.length : 0;
      const avgScore = opps.length > 0 ? opps.reduce((s, o: any) => s + (o.match_score || 0), 0) / opps.length : 0;
      const revenue = (billingRes.data || []).reduce((s: number, l: any) => s + (l.final_cents || 0), 0);

      setKpis({
        totalProviders: providers.length,
        activeProviders: (activeRes.data || []).length,
        newRegistrations7d: (regsRes.data || []).length,
        avgAcceptanceRate: Math.round(avgAcceptance * 100),
        totalOpps30d: opps.length,
        deliveredOpps30d: opps.filter((o: any) => o.delivery_state === "DELIVERED" || o.delivery_state === "sent").length,
        monthRevenue: revenue / 100,
        avgMatchScore: Math.round(avgScore),
      });
    } catch (err) {
      console.error("Sales KPI load error:", err);
    } finally {
      setLoading(false);
    }
  }

  function copyToClipboard(text: string, idx: number) {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  }

  const QUICK_ARGUMENTS = [
    { title: t("op.salesArgRiskFree"), text: t("op.salesArgRiskFreeDesc"), icon: CheckCircle },
    { title: t("op.salesArgQuality"), text: t("op.salesArgQualityDesc"), icon: Star },
    { title: t("op.salesArgFairness"), text: t("op.salesArgFairnessDesc"), icon: Users },
    { title: t("op.salesArgFast"), text: t("op.salesArgFastDesc"), icon: Clock },
    { title: t("op.salesArgFlexible"), text: t("op.salesArgFlexibleDesc"), icon: Target },
  ];

  return (
    <OperatorLayout title={t("op.salesTitle")} subtitle={t("op.salesSubtitle")}>
      {loading ? (
        <p className="text-caption">{t("common.loading")}</p>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: t("op.salesTotalProviders"), value: kpis?.totalProviders || 0, icon: Users },
              { label: t("op.salesActiveProviders"), value: kpis?.activeProviders || 0, icon: CheckCircle },
              { label: t("op.salesNewRegs7d"), value: kpis?.newRegistrations7d || 0, icon: TrendingUp },
              { label: t("op.salesAvgAcceptance"), value: `${kpis?.avgAcceptanceRate || 0} %`, icon: Target },
              { label: t("op.salesOpps30d"), value: kpis?.totalOpps30d || 0, icon: Target },
              { label: t("op.salesDelivered30d"), value: kpis?.deliveredOpps30d || 0, icon: CheckCircle },
              { label: t("op.salesRevenue"), value: `${(kpis?.monthRevenue || 0).toFixed(0)} €`, icon: TrendingUp },
              { label: t("op.salesAvgScore"), value: kpis?.avgMatchScore || 0, icon: Star },
            ].map((item) => (
              <Card key={item.label}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-micro">{item.label}</p>
                  </div>
                  <p className="text-2xl font-medium">{item.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("op.salesElevatorPitch")}</CardTitle>
            </CardHeader>
            <CardContent>
              <blockquote className="border-l-4 border-accent pl-4 italic text-sm text-muted-foreground">{ELEVATOR_PITCH}</blockquote>
              <Button variant="ghost" size="sm" className="mt-3" onClick={() => copyToClipboard(ELEVATOR_PITCH, -1)}>
                {copiedIdx === -1 ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copiedIdx === -1 ? t("op.salesCopied") : t("op.salesCopy")}
              </Button>
            </CardContent>
          </Card>

          <div>
            <h2 className="text-subheadline mb-4">{t("op.salesTop5")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {QUICK_ARGUMENTS.map((arg) => (
                <Card key={arg.title}>
                  <CardContent className="p-4">
                    <arg.icon className="w-5 h-5 text-accent mb-2" />
                    <p className="font-medium text-sm mb-1">{arg.title}</p>
                    <p className="text-micro text-muted-foreground">{arg.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("op.salesStrongestNumbers")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-display text-accent">94 %</p>
                  <p className="text-micro mt-1">{t("op.salesAcceptanceRate")}</p>
                </div>
                <div className="text-center">
                  <p className="text-display text-accent">&lt; 4h</p>
                  <p className="text-micro mt-1">{t("op.salesResponseTime")}</p>
                </div>
                <div className="text-center">
                  <p className="text-display text-accent">3–5</p>
                  <p className="text-micro mt-1">{t("op.salesProvidersPerRequest")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div>
            <h2 className="text-subheadline mb-4">{t("op.salesObjectionHandling")}</h2>
            <div className="space-y-3">
              {OBJECTION_RESPONSES.map((item, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-sm text-destructive mb-1">„{item.objection}"</p>
                        <p className="text-sm text-muted-foreground">{item.response}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="shrink-0" onClick={() => copyToClipboard(item.response, i)}>
                        {copiedIdx === i ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("op.salesBenchmarks")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 font-medium">{t("op.salesKPI")}</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("op.salesBenchmark")}</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border"><td className="py-2 pr-4">{t("op.salesBenchCostPerLead")}</td><td className="py-2">39–399 €</td></tr>
                    <tr className="border-b border-border"><td className="py-2 pr-4">{t("op.salesBenchConvChat")}</td><td className="py-2">60–80 %</td></tr>
                    <tr className="border-b border-border"><td className="py-2 pr-4">{t("op.salesBenchConvCustomer")}</td><td className="py-2">25–40 %</td></tr>
                    <tr className="border-b border-border"><td className="py-2 pr-4">{t("op.salesBenchCAC")}</td><td className="py-2">150–600 €</td></tr>
                    <tr><td className="py-2 pr-4">ROI</td><td className="py-2">300–1.500 %</td></tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </OperatorLayout>
  );
}
